import { Eye, EyeOff, Pencil, Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { panelsForFeature, type Panel } from '@/constants/navigation';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { FeatureSwitchPayload, InstituteFeature } from '@/types/platformFeature';

interface FeatureChecklistProps {
  features: InstituteFeature[];
  /** The uuid currently being written, so only that row goes busy. */
  pendingUuid: string | null;
  /** True while a bulk action is in flight — every row is held. */
  isBusy: boolean;
  /** The rows ticked for adding. Tracked features are never in here. */
  selected: Set<string>;
  onToggleSelect: (featureUuid: string) => void;
  onSelectAll: (next: boolean) => void;
  onAddSelected: () => void;
  onSet: (featureUuid: string, payload: FeatureSwitchPayload) => void;
  onRemove: (featureUuid: string) => void;
  onEdit: (feature: InstituteFeature) => void;
  /**
   * Whether the viewer may change anything. Every write behind these controls
   * is SUPER_ADMIN only, so support is shown the same list as a record — what
   * was asked for and what is active — with no box, eye or × to press.
   */
  canEdit: boolean;
}

/** One colour per panel, so who a feature is for reads at a glance. */
const PANEL_TONE: Record<Panel, string> = {
  Admin: 'bg-panel-admin-bg text-panel-admin',
  Teacher: 'bg-panel-teacher-bg text-panel-teacher',
  Student: 'bg-panel-student-bg text-panel-student',
  Parent: 'bg-panel-parent-bg text-panel-parent',
};

/**
 * The colour down the left of a row, which is what makes the list scannable.
 *
 * Three states, read at a glance without stopping on the controls:
 *
 *   active      green — on their list and switched on.
 *   inactive    accent — on their list, not switched on.
 *   untracked   nothing — the institute has not asked for it.
 */
function edgeFor(feature: InstituteFeature): string {
  if (!feature.isTracked) return 'border-l-transparent';
  return feature.isEnabled ? 'border-l-ok' : 'border-l-accent-line';
}

/**
 * What one institute asked for — every feature, one after another.
 *
 * One flat list rather than a section per category: the reader is going down
 * the whole catalogue deciding what this customer needs, and headings between
 * the rows only break that read up. The order is the one the server sent, which
 * is the catalogue's own; sorting again here would be a second opinion about it
 * (CLAUDE.md §3).
 *
 * Every row carries a box on the left. On a feature this institute has not
 * asked for it selects it, and one Add button at the top records every ticked
 * one in a single request. On a feature already on their list the box is ticked
 * and disabled, which is what says "this one is already in" without a second
 * kind of marker to learn.
 *
 * A tracked row carries one control on the right, the eye: whether they can
 * use it. Clicking it is the action — it is the control, not a picture of the
 * state beside a switch.
 */
export function FeatureChecklist({
  features,
  pendingUuid,
  isBusy,
  selected,
  onToggleSelect,
  onSelectAll,
  onAddSelected,
  onSet,
  onRemove,
  onEdit,
  canEdit,
}: FeatureChecklistProps) {
  const selectable = features.filter((feature) => !feature.isTracked);
  const allSelected = selectable.length > 0 && selected.size === selectable.length;
  const someSelected = selected.size > 0 && !allSelected;

  return (
    <div className="flex flex-col">
      {/*
        Stays put while the list scrolls: the Add button belongs with the boxes
        being ticked, and twenty rows is enough to lose it off the top.
        Full-bleed through the card's own padding so the rule meets both edges.
      */}
      {canEdit && (
        <div
          className={cn(
            'sticky top-0 z-10 -mx-5 -mt-5 mb-3 flex flex-wrap items-center justify-between gap-3',
            'rounded-t-card border-b border-line bg-surface px-5 py-3',
          )}
        >
          <div className="flex items-center gap-2">
            <Checkbox
              label={`Select all ${selectable.length} features not yet added`}
              hideLabel
              checked={allSelected}
              indeterminate={someSelected}
              disabled={isBusy || selectable.length === 0}
              onChange={(event) => onSelectAll(event.target.checked)}
            />
            <span className="text-xs text-ink-2">
              {selectable.length === 0
                ? 'Every feature is on this institute’s list'
                : `Select all (${selectable.length} not added)`}
            </span>
          </div>

          <Button
            size="sm"
            disabled={isBusy || selected.size === 0}
            onClick={onAddSelected}
            leftIcon={<Plus className="h-3.5 w-3.5" />}
          >
            Add selected{selected.size > 0 ? ` (${selected.size})` : ''}
          </Button>
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {features.map((feature) => {
          const isPending = pendingUuid === feature.uuid;
          const isHeld = isBusy || isPending;
          const isSelected = selected.has(feature.uuid);

          return (
            <li
              key={feature.uuid}
              className={cn(
                'rounded-field border border-l-2 p-3 transition-colors duration-150',
                edgeFor(feature),
                feature.isTracked
                  ? 'border-line bg-surface hover:border-line-strong'
                  : 'border-dashed border-line bg-surface-2 hover:bg-surface-3',
                isSelected && !feature.isTracked && 'border-solid border-accent-line bg-accent-soft',
                isPending && 'opacity-60',
              )}
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  {canEdit && (
                    <span className="pt-0.5">
                      <Checkbox
                        label={
                          feature.isTracked
                            ? `${feature.name} is already on this institute’s list`
                            : `Select ${feature.name}`
                        }
                        hideLabel
                        checked={feature.isTracked || isSelected}
                        // A tracked feature is shown ticked and locked: it is
                        // already in, and unticking it here would be mistaken for
                        // withdrawing it — which is what the × on the right does.
                        disabled={feature.isTracked || isHeld}
                        title={feature.isTracked ? 'Already added' : undefined}
                        onChange={() => onToggleSelect(feature.uuid)}
                      />
                    </span>
                  )}

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p
                        className={cn(
                          'text-sm font-[560]',
                          feature.isTracked ? 'text-ink' : 'text-ink-2',
                        )}
                      >
                        {feature.name}
                      </p>
                      <span className="pill bg-surface-2 text-ink-3">{feature.key}</span>
                      {/* The list price; a feature without one comes with the
                          base licence, which is said rather than shown as ₹0. */}
                      <span
                        className={cn(
                          'pill',
                          feature.price === null ? 'bg-surface-2 text-ink-3' : 'bg-accent-soft text-accent',
                        )}
                      >
                        {feature.price === null ? 'Base licence' : formatCurrency(feature.price)}
                      </span>
                      {/* Who sees it — information only, taken from the menus. */}
                      {panelsForFeature(feature.key).map((panel) => (
                        <span key={panel} className={cn('pill', PANEL_TONE[panel])}>
                          <span className="sr-only">Shown on the </span>
                          {panel}
                          <span className="sr-only"> panel</span>
                        </span>
                      ))}
                    </div>
                    {feature.description && (
                      <p className="mt-1 text-xs text-ink-3">{feature.description}</p>
                    )}
                  </div>
                </div>

                <div
                  className={cn(
                    'flex shrink-0 flex-wrap items-center gap-2 lg:pl-0',
                    canEdit && 'pl-8',
                  )}
                >
                  {feature.isTracked && !canEdit && (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-pill px-2 py-1 text-xs',
                        feature.isEnabled ? 'bg-accent-soft font-[560] text-accent-2' : 'text-ink-3',
                      )}
                    >
                      {feature.isEnabled ? (
                        <Eye className="h-3.5 w-3.5" aria-hidden />
                      ) : (
                        <EyeOff className="h-3.5 w-3.5" aria-hidden />
                      )}
                      {feature.isEnabled ? 'Active' : 'Inactive'}
                    </span>
                  )}

                  {feature.isTracked && canEdit && (
                    <div className="flex items-center gap-3 rounded-field border border-line bg-surface-2 px-3 py-1.5">
                      {/*
                        The eye is the control, not a picture of the state.
                        `aria-pressed` is what keeps it a toggle rather than a
                        plain button to a reader.
                      */}
                      <button
                        type="button"
                        aria-pressed={feature.isEnabled}
                        disabled={isHeld}
                        onClick={() => onSet(feature.uuid, { isEnabled: !feature.isEnabled })}
                        title={
                          feature.isEnabled
                            ? `Hide ${feature.name} from this institute`
                            : `Make ${feature.name} active for this institute`
                        }
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-pill px-2 py-1 text-xs',
                          'transition-colors duration-150',
                          'focus:outline-none focus:ring-2 focus:ring-accent',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                          feature.isEnabled
                            ? 'bg-accent-soft font-[560] text-accent-2 hover:ring-1 hover:ring-inset hover:ring-accent'
                            : 'text-ink-3 hover:bg-surface-3 hover:text-ink-2',
                        )}
                      >
                        {feature.isEnabled ? (
                          <Eye className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" aria-hidden />
                        )}
                        {feature.isEnabled ? 'Active' : 'Inactive'}
                      </button>
                    </div>
                  )}

                  {/* Edits the catalogue, not this institute's copy — every
                      customer reads the wording being changed here. */}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => onEdit(feature)}
                      disabled={isHeld}
                      title={`Edit ${feature.name}`}
                      className={cn(
                        'rounded-field border border-transparent p-2 text-ink-3',
                        'transition-colors duration-150',
                        'hover:border-line hover:bg-surface-2 hover:text-accent',
                        'focus:outline-none focus:ring-2 focus:ring-accent',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                      )}
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                      <span className="sr-only">Edit {feature.name}</span>
                    </button>
                  )}

                  {feature.isTracked && canEdit && (
                    <button
                      type="button"
                      onClick={() => onRemove(feature.uuid)}
                      disabled={isHeld}
                      title={`Withdraw ${feature.name} from this institute's requirements`}
                      className={cn(
                        'rounded-field border border-transparent p-2 text-ink-3',
                        'transition-colors duration-150',
                        'hover:border-crit hover:bg-crit-bg hover:text-crit',
                        'focus:outline-none focus:ring-2 focus:ring-accent',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                      )}
                    >
                      <X className="h-4 w-4" aria-hidden />
                      <span className="sr-only">Withdraw {feature.name}</span>
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The bar above the list: where the institute stands, and the "all of them"
 * switch.
 *
 * Split out rather than inlined because it reads against the checklist below
 * it, and keeping it here means the page file stays about what happens rather
 * than about what it looks like.
 */
export function ChecklistToolbar({
  tracked,
  enabled,
  isBusy,
  onSetAllEnabled,
}: {
  tracked: number;
  enabled: number;
  isBusy: boolean;
  /** Left out for a viewer who may not change the board, which hides the switch. */
  onSetAllEnabled?: (next: boolean) => void;
}) {
  const allEnabled = tracked > 0 && enabled === tracked;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
      <p className="text-xs text-ink-3">
        {tracked === 0
          ? onSetAllEnabled
            ? 'Nothing tracked yet — tick what this institute has asked for and add it.'
            : 'Nothing tracked yet.'
          : `${enabled} of ${tracked} active`}
      </p>

      {onSetAllEnabled && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={isBusy || tracked === 0}
            onClick={() => onSetAllEnabled(!allEnabled)}
            leftIcon={
              allEnabled ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />
            }
          >
            {allEnabled ? 'Deactivate all' : 'Activate all'}
          </Button>
        </div>
      )}
    </div>
  );
}
